import { MediaUrlInfo, PostMediaFile } from "shared/types/post";
import { FIELDS } from "constants/PostFormFields";

/**
 * Determines if a click event should be ignored by PostForm's click-outside handler.
 * Returns true for clicks inside modals, Radix portals (popovers, menus, dialogs).
 */
export function shouldIgnoreClick(event: MouseEvent): boolean {
  const path = event.composedPath() || [];
  return [...path].some((elem) => {
    if (!(elem instanceof HTMLElement)) return false;
    // Check for modal classNames (existing behavior)
    if (elem.className && /modal/.test(elem.className)) return true;
    // Check for Radix popovers and menus
    if (elem.hasAttribute?.("data-radix-popper-content-wrapper")) return true;
    if (elem.hasAttribute?.("data-radix-menu-content")) return true;
    // Check for Radix Dialog portals (e.g. media picker modal)
    const slot = elem.getAttribute?.("data-slot");
    if (slot === "dialog-content" || slot === "dialog-overlay") return true;
    // Sheet portals (e.g. the alt-text drawer) render outside the form tree;
    // clicking inside one must not trigger the form's close confirmation.
    if (slot === "sheet-content" || slot === "sheet-overlay") return true;
    return false;
  });
}

/** sometimes post.mediaFiles is empty and media is stored in post form itself,
 * this will happen when create a scheduled post with single image then edit that post again
 * This function is to unify the way to extract media urls from post
 */
export function extractMediaUrls(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  post: Record<string, any> | undefined
): MediaUrlInfo[] {
  if (!post) {
    return [];
  }

  const mediaFiles = post[FIELDS.MEDIA] as PostMediaFile[];
  if (mediaFiles && mediaFiles.length > 0) {
    return mediaFiles;
  }

  if (post.url && post.meta) {
    return [
      {
        url: post.url,
        meta: post.meta,
        isVideo: post.isVideo || post.meta?.isVideo,
        type: post.meta?.type,
        thumbnailUrl: post.thumbnailUrl,
      },
    ];
  }
  return [];
}
